import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FiFolder,
  FiCheckSquare,
  FiAlertCircle,
  FiClock,
  FiFileText,
  FiSend,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiPlus,
  FiX,
  FiLayers,
  FiUser,
  FiCalendar,
  FiArrowRight,
} from 'react-icons/fi';
import StatusDropdown from '../components/StatusDropdown';
import AssigneePicker from '../components/AssigneePicker';
import TaskDetailModal from '../components/TaskDetailModal';
import { getTypeMeta } from '../lib/sharedListTypes';
import './Dashboard.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const keyFor = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
const sameDay = (a, b) =>
  !!a && !!b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const Dashboard = ({ token, user }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [addingGlobal, setAddingGlobal] = useState(false);
  const [newGlobal, setNewGlobal] = useState({ title: '', dueDate: '' });
  const [savingGlobal, setSavingGlobal] = useState(false);
  const [sharedListsData, setSharedListsData] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchSharedListsDashboard = useCallback(async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shared-lists/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.ok) {
        const result = await r.json();
        setSharedListsData(result);
      }
    } catch (err) {
      console.error('Failed to fetch shared lists dashboard:', err);
    }
  }, [token]);

  useEffect(() => { fetchDashboardData(); }, [fetchDashboardData]);
  useEffect(() => { fetchSharedListsDashboard(); }, [fetchSharedListsDashboard]);

  // ---------- Calendar dot map ----------
  const dotMap = useMemo(() => {
    const map = {};
    const visible = data?.visibleTasks || [];
    for (const t of visible) {
      if (!t.dueDate) continue;
      const d = new Date(t.dueDate);
      if (Number.isNaN(d.getTime())) continue;
      const k = keyFor(d);
      if (!map[k]) map[k] = { hasAny: false, hasMine: false, tasks: [] };
      map[k].hasAny = true;
      if (t.isMine) map[k].hasMine = true;
      map[k].tasks.push(t);
    }
    return map;
  }, [data]);

  // ---------- Update item status (board task) ----------
  const updateItemStatus = async (taskId, newStatus) => {
    setData((prev) => prev && ({
      ...prev,
      visibleTasks: (prev.visibleTasks || []).map((t) => t.id === taskId ? { ...t, status: newStatus } : t),
      myTasks: (prev.myTasks || []).map((t) => t.id === taskId ? { ...t, status: newStatus } : t),
    }));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/items/${taskId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) fetchDashboardData();
    } catch {
      fetchDashboardData();
    }
  };

  // ---------- Global tasks ----------
  const updateGlobalStatus = async (taskId, newStatus) => {
    setData((prev) => prev && ({
      ...prev,
      globalTasks: (prev.globalTasks || []).map((t) => t.id === taskId ? { ...t, status: newStatus } : t),
    }));
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/global-tasks/${taskId}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) fetchDashboardData();
    } catch {
      fetchDashboardData();
    }
  };

  const submitNewGlobal = async (e) => {
    e.preventDefault();
    const title = newGlobal.title.trim();
    if (!title) return;
    setSavingGlobal(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/global-tasks`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          description: '',
          // Self-assigned so it shows in this dashboard
          assignee: user?.id || '',
          dueDate: newGlobal.dueDate || null,
          status: 'Not Started',
        }),
      });
      if (res.ok) {
        setNewGlobal({ title: '', dueDate: '' });
        setAddingGlobal(false);
        fetchDashboardData();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to create global task');
      }
    } finally {
      setSavingGlobal(false);
    }
  };

  if (loading) return <div className="loading-state" data-testid="dashboard-loading">Loading dashboard...</div>;
  if (!data) return <div className="loading-state">Failed to load dashboard data</div>;

  const myTasks = (data.myTasks || []).filter((t) => t.status !== 'Done');
  const globalTasks = (data.globalTasks || []).filter((t) => t.status !== 'Done');
  const metrics = data.metrics || {};
  const recentBoards = data.recentBoards || [];

  // Group my tasks by board
  const myTasksByBoard = {};
  for (const t of myTasks) {
    const key = t.boardId || '_other';
    if (!myTasksByBoard[key]) {
      myTasksByBoard[key] = { boardId: key, boardName: t.boardName || 'Other', boardColor: t.boardColor, tasks: [] };
    }
    myTasksByBoard[key].tasks.push(t);
  }
  const myBoardGroups = Object.values(myTasksByBoard).sort((a, b) => a.boardName.localeCompare(b.boardName));

  // Calendar rendering
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDow = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const today = new Date();

  const calendarCells = [];
  for (let i = 0; i < startDow; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(new Date(year, month, d));

  const selectedTasks = selectedDay ? (dotMap[keyFor(selectedDay)]?.tasks || []) : [];

  return (
    <div className="dashboard" data-testid="dashboard">
      {/* ============ CALENDAR ============ */}
      <section className="dash-calendar-card" data-testid="dash-calendar-card">
        <header className="dash-calendar-head">
          <div className="dash-calendar-month">
            <button
              type="button"
              className="dash-cal-nav-btn"
              onClick={() => setCurrentMonth(new Date(year, month - 1, 1))}
              aria-label="Previous month"
              data-testid="dash-cal-prev"
            >
              <FiChevronLeft size={18} />
            </button>
            <h2 className="dash-cal-title">{MONTHS[month]} {year}</h2>
            <button
              type="button"
              className="dash-cal-nav-btn"
              onClick={() => setCurrentMonth(new Date(year, month + 1, 1))}
              aria-label="Next month"
              data-testid="dash-cal-next"
            >
              <FiChevronRight size={18} />
            </button>
          </div>
          <div className="dash-cal-legend">
            <span className="dash-cal-legend-item">
              <span className="dash-cal-legend-dot dash-cal-legend-dot-black" />
              Has tasks
            </span>
            <span className="dash-cal-legend-item">
              <span className="dash-cal-legend-dot dash-cal-legend-dot-red" />
              Assigned to me
            </span>
          </div>
        </header>

        <div className="dash-cal-weekdays">
          {DAYS.map((d, i) => (<div key={i} className="dash-cal-weekday">{d}</div>))}
        </div>

        <div className="dash-cal-grid" data-testid="dash-cal-grid">
          {calendarCells.map((date, i) => {
            if (!date) return <div key={`e-${i}`} className="dash-cal-cell empty" />;
            const info = dotMap[keyFor(date)];
            const isToday = sameDay(date, today);
            const isSelected = sameDay(date, selectedDay);
            const classes = [
              'dash-cal-cell',
              isToday ? 'today' : '',
              isSelected ? 'selected' : '',
              info?.hasAny ? 'has-tasks' : '',
            ].filter(Boolean).join(' ');
            return (
              <button
                type="button"
                key={keyFor(date)}
                className={classes}
                onClick={() => {
                  if (!info) return setSelectedDay(null);
                  setSelectedDay(isSelected ? null : date);
                }}
                aria-label={`${MONTHS[month]} ${date.getDate()}${info ? `, ${info.tasks.length} tasks` : ''}`}
                data-testid={`dash-cal-day-${year}-${month + 1}-${date.getDate()}`}
              >
                <span className="dash-cal-day-num">{date.getDate()}</span>
                {info && (
                  <span className="dash-cal-dots" aria-hidden="true">
                    {info.hasAny && <span className="dash-cal-dot dash-cal-dot-black" />}
                    {info.hasMine && <span className="dash-cal-dot dash-cal-dot-red" />}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {selectedDay && selectedTasks.length > 0 && (
          <div className="dash-day-panel" data-testid="dash-day-panel">
            <div className="dash-day-panel-head">
              <div className="dash-day-panel-title">
                {selectedDay.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                <span className="dash-day-panel-count">{selectedTasks.length} task{selectedTasks.length === 1 ? '' : 's'}</span>
              </div>
              <button
                type="button"
                className="dash-day-panel-close"
                onClick={() => setSelectedDay(null)}
                aria-label="Close"
                data-testid="dash-day-panel-close"
              >
                <FiX size={14} />
              </button>
            </div>
            <ul className="dash-day-task-list">
              {selectedTasks.map((t) => {
                const isDone = t.status === 'Done';
                return (
                  <li key={t.id} className={`dash-day-task ${t.isMine ? 'is-mine' : ''} ${isDone ? 'is-done' : ''}`} data-testid={`dash-day-task-${t.id}`}>
                    <label className="dash-day-task-check">
                      <input
                        type="checkbox"
                        checked={isDone}
                        onChange={(e) => updateItemStatus(t.id, e.target.checked ? 'Done' : 'Not Started')}
                        aria-label={`Mark ${t.name} as ${isDone ? 'not done' : 'done'}`}
                        data-testid={`dash-day-task-check-${t.id}`}
                      />
                    </label>
                    <button
                      type="button"
                      className="dash-day-task-body"
                      onClick={() => setActiveTask(t)}
                    >
                      <span className="dash-day-task-name">{t.name}</span>
                      <span className="dash-day-task-meta">
                        <span className="dash-day-task-board" style={{ borderColor: t.boardColor || '#999' }}>{t.boardName}</span>
                        {t.isMine && <span className="dash-day-task-pill-mine">Assigned to me</span>}
                      </span>
                    </button>
                    <div className="dash-day-task-status">
                      <StatusDropdown
                        value={t.status}
                        onChange={(s) => updateItemStatus(t.id, s)}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </section>

      {/* ============ MY TASKS BY BOARD ============ */}
      <section className="dash-card" data-testid="dash-my-tasks-card">
        <h2 className="dash-card-title"><FiCheckSquare size={18} /> My Tasks</h2>
        {myBoardGroups.length === 0 ? (
          <div className="dash-empty">No tasks assigned to you. Enjoy the calm! 🌿</div>
        ) : (
          <div className="dash-board-groups">
            {myBoardGroups.map((bg) => (
              <div key={bg.boardId} className="dash-board-group" data-testid={`dash-board-group-${bg.boardId}`}>
                <div className="dash-board-group-head">
                  <span className="dash-board-color" style={{ background: bg.boardColor || '#0F0F10' }} />
                  <Link to={`/boards/${bg.boardId}`} className="dash-board-name">{bg.boardName}</Link>
                  <span className="dash-board-count">{bg.tasks.length} task{bg.tasks.length === 1 ? '' : 's'}</span>
                </div>
                <ul className="dash-task-list">
                  {bg.tasks.map((t) => {
                    const isDone = t.status === 'Done';
                    return (
                      <li key={t.id} className={`dash-task ${isDone ? 'is-done' : ''}`} data-testid={`dash-my-task-${t.id}`}>
                        <label className="dash-task-check">
                          <input
                            type="checkbox"
                            checked={isDone}
                            onChange={(e) => updateItemStatus(t.id, e.target.checked ? 'Done' : 'Not Started')}
                            aria-label={`Mark ${t.name} as ${isDone ? 'not done' : 'done'}`}
                            data-testid={`dash-my-task-check-${t.id}`}
                          />
                        </label>
                        <button
                          type="button"
                          className="dash-task-body"
                          onClick={() => setActiveTask({ ...t, boardId: bg.boardId })}
                        >
                          <span className="dash-task-name">{t.name}</span>
                          {t.dueDate && (
                            <span className="dash-task-due">{new Date(t.dueDate).toLocaleDateString()}</span>
                          )}
                        </button>
                        <div className="dash-task-status">
                          <StatusDropdown
                            value={t.status}
                            onChange={(s) => updateItemStatus(t.id, s)}
                          />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ============ MY GLOBAL TASKS ============ */}
      <section className="dash-card" data-testid="dash-global-tasks-card">
        <div className="dash-card-head-row">
          <h2 className="dash-card-title"><FiSend size={18} /> Global Tasks Assigned to Me</h2>
          {!addingGlobal && (
            <button
              type="button"
              className="dash-add-btn"
              onClick={() => setAddingGlobal(true)}
              data-testid="dash-add-global-btn"
            >
              <FiPlus size={14} /> Add Task
            </button>
          )}
        </div>

        {addingGlobal && (
          <form className="dash-add-global-form" onSubmit={submitNewGlobal} data-testid="dash-add-global-form">
            <input
              className="input"
              autoFocus
              type="text"
              placeholder="Task title"
              value={newGlobal.title}
              onChange={(e) => setNewGlobal({ ...newGlobal, title: e.target.value })}
              data-testid="dash-add-global-title"
            />
            <input
              className="input"
              type="date"
              value={newGlobal.dueDate}
              onChange={(e) => setNewGlobal({ ...newGlobal, dueDate: e.target.value })}
              data-testid="dash-add-global-due"
            />
            <button type="submit" className="btn btn-primary" disabled={savingGlobal} data-testid="dash-add-global-submit">
              {savingGlobal ? 'Adding…' : 'Add'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => { setAddingGlobal(false); setNewGlobal({ title: '', dueDate: '' }); }}
            >
              Cancel
            </button>
          </form>
        )}

        {globalTasks.length === 0 ? (
          <div className="dash-empty">No open global tasks assigned to you.</div>
        ) : (
          <ul className="dash-task-list">
            {globalTasks.map((t) => {
              const isDone = t.status === 'Done';
              return (
                <li key={t.id} className={`dash-task ${isDone ? 'is-done' : ''}`} data-testid={`dash-global-task-${t.id}`}>
                  <label className="dash-task-check">
                    <input
                      type="checkbox"
                      checked={isDone}
                      onChange={(e) => updateGlobalStatus(t.id, e.target.checked ? 'Done' : 'Not Started')}
                      aria-label={`Mark ${t.title} as ${isDone ? 'not done' : 'done'}`}
                      data-testid={`dash-global-task-check-${t.id}`}
                    />
                  </label>
                  <div className="dash-task-body" style={{ cursor: 'default' }}>
                    <span className="dash-task-name">{t.title}</span>
                    {t.dueDate && (
                      <span className="dash-task-due">Due {new Date(t.dueDate).toLocaleDateString()}</span>
                    )}
                  </div>
                  <div className="dash-task-status">
                    <StatusDropdown
                      value={t.status}
                      onChange={(s) => updateGlobalStatus(t.id, s)}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* ============ SHARED LISTS WIDGET ============ */}
      <SharedListsWidget data={sharedListsData} />

      {/* ============ REPORTS (collapsible) ============ */}
      <section className="dash-card dash-reports-card">
        <button
          type="button"
          className="dash-reports-toggle"
          onClick={() => setReportsOpen((v) => !v)}
          aria-expanded={reportsOpen}
          data-testid="dash-reports-toggle"
        >
          <span>Reports & Quick Stats</span>
          <FiChevronDown size={18} className={`dash-reports-chevron ${reportsOpen ? 'open' : ''}`} />
        </button>

        {reportsOpen && (
          <div className="dash-reports-content" data-testid="dash-reports-content">
            <div className="metrics-grid">
              <Metric icon={<FiFolder size={20} />} label="Boards" value={metrics.totalBoards} sub={`${metrics.activeBoards || 0} active`} />
              <Metric icon={<FiCheckSquare size={20} />} label="Tasks" value={metrics.totalTasks} sub={`${metrics.completedTasks || 0} completed`} />
              <Metric icon={<FiAlertCircle size={20} />} label="Overdue" value={metrics.overdueTasks} tone={metrics.overdueTasks > 0 ? 'warn' : ''} />
              <Metric icon={<FiClock size={20} />} label="Blocked" value={metrics.blockedTasks} tone={metrics.blockedTasks > 0 ? 'warn' : ''} />
              <Metric icon={<FiFileText size={20} />} label="Open RFIs" value={metrics.openRFIs} />
              <Metric icon={<FiSend size={20} />} label="Open Submittals" value={metrics.openSubmittals} />
            </div>

            {recentBoards.length > 0 && (
              <div className="dash-recent-boards">
                <h3 className="dash-sub-title">Recent Boards</h3>
                <ul className="dash-recent-board-list">
                  {recentBoards.map((b) => (
                    <li key={b.id}>
                      <Link to={`/boards/${b.id}`} className="dash-recent-board-link">
                        <span className="dash-board-color" style={{ background: b.color || '#0F0F10' }} />
                        <span>{b.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Task detail modal */}
      {activeTask && (
        <TaskDetailModal
          isOpen={true}
          task={activeTask}
          onClose={() => setActiveTask(null)}
        />
      )}
    </div>
  );
};

const Metric = ({ icon, label, value, sub, tone }) => (
  <div className={`metric-card ${tone || ''}`} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="metric-icon">{icon}</div>
    <div className="metric-content">
      <div className="metric-value">{value ?? 0}</div>
      <div className="metric-label">{label}</div>
      {sub && <div className="metric-sub">{sub}</div>}
    </div>
  </div>
);

// ============ Shared Lists Widget ============
const SharedListsWidget = ({ data }) => {
  const assigned = data?.assignedToMe || [];
  const lists = data?.myLists || [];
  const dueSoon = data?.dueSoon || [];
  const hasAny = assigned.length || lists.length || dueSoon.length;

  return (
    <section className="dash-card sl-widget" data-testid="dash-shared-lists-widget">
      <header className="sl-widget-header">
        <div className="sl-widget-title-row">
          <FiLayers size={18} className="sl-widget-icon" />
          <h2 className="dash-section-title sl-widget-title">Shared Lists</h2>
        </div>
        <Link to="/shared-lists" className="sl-widget-link" data-testid="sl-widget-view-all">
          View all <FiArrowRight size={14} />
        </Link>
      </header>

      {!data ? (
        <div className="sl-widget-loading">Loading...</div>
      ) : !hasAny ? (
        <div className="sl-widget-empty" data-testid="sl-widget-empty">
          <div className="sl-widget-empty-title">No shared list activity yet</div>
          <div className="sl-widget-empty-text">
            Create a shared list to track checklists, snags, shopping lists and more.
          </div>
          <Link to="/shared-lists" className="sl-widget-empty-cta" data-testid="sl-widget-create">
            <FiPlus size={14} /> Open Shared Lists
          </Link>
        </div>
      ) : (
        <div className="sl-widget-grid">
          {/* --- Assigned to me --- */}
          <div className="sl-widget-col" data-testid="sl-widget-assigned">
            <div className="sl-widget-col-head">
              <FiUser size={14} />
              <span>Assigned to me</span>
              <span className="sl-widget-badge">{assigned.length}</span>
            </div>
            {assigned.length === 0 ? (
              <div className="sl-widget-col-empty">Nothing assigned yet.</div>
            ) : (
              <ul className="sl-widget-list">
                {assigned.slice(0, 6).map((it) => (
                  <SLItemRow key={it.id} item={it} showDue />
                ))}
              </ul>
            )}
          </div>

          {/* --- My open lists --- */}
          <div className="sl-widget-col" data-testid="sl-widget-mylists">
            <div className="sl-widget-col-head">
              <FiLayers size={14} />
              <span>My open lists</span>
              <span className="sl-widget-badge">{lists.length}</span>
            </div>
            {lists.length === 0 ? (
              <div className="sl-widget-col-empty">You haven&apos;t created any lists.</div>
            ) : (
              <ul className="sl-widget-list">
                {lists.slice(0, 6).map((l) => {
                  const meta = getTypeMeta(l.type);
                  const Icon = meta?.icon;
                  return (
                    <li key={l.id} className="sl-widget-list-row" data-testid="sl-widget-list-row">
                      <Link to={`/shared-lists/${l.id}`} className="sl-widget-row-link">
                        <span
                          className="sl-widget-row-icon"
                          style={{ background: `${meta?.color || '#64748b'}1A`, color: meta?.color || '#64748b' }}
                        >
                          {Icon ? <Icon size={13} /> : <FiLayers size={13} />}
                        </span>
                        <span className="sl-widget-row-main">
                          <span className="sl-widget-row-title">{l.title}</span>
                          <span className="sl-widget-row-sub">
                            {l.openCount} open · {l.totalCount} total
                          </span>
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* --- Board-linked due soon --- */}
          <div className="sl-widget-col" data-testid="sl-widget-due-soon">
            <div className="sl-widget-col-head">
              <FiCalendar size={14} />
              <span>Due soon (board-linked)</span>
              <span className="sl-widget-badge">{dueSoon.length}</span>
            </div>
            {dueSoon.length === 0 ? (
              <div className="sl-widget-col-empty">No board-linked items due in 7 days.</div>
            ) : (
              <ul className="sl-widget-list">
                {dueSoon.slice(0, 6).map((it) => (
                  <SLItemRow key={it.id} item={it} showDue showBoard />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

const SLItemRow = ({ item, showDue, showBoard }) => {
  const meta = getTypeMeta(item.listType);
  const Icon = meta?.icon;
  const due = item.dueDate ? new Date(item.dueDate) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isOverdue = due && due < today;
  return (
    <li className="sl-widget-list-row" data-testid="sl-widget-item-row">
      <Link to={`/shared-lists/${item.listId}`} className="sl-widget-row-link">
        <span
          className="sl-widget-row-icon"
          style={{ background: `${meta?.color || '#64748b'}1A`, color: meta?.color || '#64748b' }}
          title={meta?.label || ''}
        >
          {Icon ? <Icon size={13} /> : <FiLayers size={13} />}
        </span>
        <span className="sl-widget-row-main">
          <span className="sl-widget-row-title">{item.text}</span>
          <span className="sl-widget-row-sub">
            <span className="sl-widget-row-list">{item.listTitle}</span>
            {showBoard && item.relatedBoardId && (
              <Link
                to={`/boards/${item.relatedBoardId}`}
                className="sl-widget-row-board"
                onClick={(e) => e.stopPropagation()}
              >
                Board
              </Link>
            )}
            {showDue && due && (
              <span className={`sl-widget-row-due ${isOverdue ? 'overdue' : ''}`}>
                {due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
              </span>
            )}
          </span>
        </span>
      </Link>
    </li>
  );
};

export default Dashboard;
